import type {
    Flight,
FlightSearchParams,
CabinClass,
TripType,
MultiCitySegment,
Passengers,
} from '@/types/travel';
import { apiFetch } from '@/lib/api';

// Environment variable to control which Duffel mode to use
const DUFFEL_MODE = import.meta.env.VITE_DUFFEL_MODE || 'test';

console.log('Duffel Environment Variables:', {
  mode: DUFFEL_MODE,
});

interface ValidatedFlightParams extends Omit<FlightSearchParams, 'essentialPrefs'> {
  tripType: TripType;
  origin?: string;
  destination?: string;
  departureDate?: string;
  returnDate?: string;
  segments?: MultiCitySegment[];
  passengers?: Passengers;
  cabinClass?: CabinClass;
}

export class DuffelClient {
  private static validateApiConfig() {
    if (!DUFFEL_MODE) {
      throw new Error('Duffel mode is not configured. Please set VITE_DUFFEL_MODE environment variable.');
    }

    if (DUFFEL_MODE !== 'test' && DUFFEL_MODE !== 'live') {
      throw new Error(`Invalid Duffel mode: ${DUFFEL_MODE}. Must be either 'test' or 'live'.`);
    }
  }

  private static validateSearchParams(params: FlightSearchParams): asserts params is ValidatedFlightParams {
    const { tripType = 'round-trip', origin, destination, departureDate, segments } = params;

    switch (tripType) {
      case 'one-way':
      case 'round-trip':
        if (!origin || !destination || !departureDate) {
          throw new Error('Missing required search parameters for one-way/round-trip');
        }
        if (origin.length !== 3 || destination.length !== 3) {
          throw new Error('Invalid airport code format');
        }
        if (tripType === 'round-trip' && !params.returnDate) {
          throw new Error('Return date is required for round-trip');
        }
        break;
      case 'multi-city':
        if (!segments || segments.length < 2) {
          throw new Error('Multi-city trips require at least 2 segments');
        }
        if (segments.length > 4) {
          throw new Error('Multi-city trips support maximum 4 segments');
        }
        segments.forEach((segment, index) => {
          if (!segment.origin || !segment.destination || !segment.departureDate) {
            throw new Error(`Missing required parameters for segment ${index + 1}`);
          }
          if (segment.origin.length !== 3 || segment.destination.length !== 3) {
            throw new Error(`Invalid airport code format in segment ${index + 1}`);
          }
        });
        break;
      default:
        throw new Error(`Invalid trip type: ${tripType}`);
    }
  }

  static async searchFlights(params: FlightSearchParams): Promise<Flight[]> {
    try {
      this.validateApiConfig();

      const searchParams = {
        ...params,
        tripType: params.tripType || 'round-trip',
      };

      this.validateSearchParams(searchParams);

      const { tripType, origin, destination, departureDate, returnDate, segments, passengers } = searchParams;

      const requestBody: any = {
        tripType,
        passengers: {
          adults: passengers?.adults || 1,
          children: passengers?.children || 0,
          infants: passengers?.infants || 0,
        },
        cabinClass: searchParams.cabinClass || 'economy',
      };

      switch (tripType) {
        case 'one-way':
          Object.assign(requestBody, { origin, destination, departureDate });
          break;
        case 'round-trip':
          Object.assign(requestBody, { origin, destination, departureDate, returnDate });
          break;
        case 'multi-city':
          requestBody.segments = segments?.map((s) => ({
            origin: s.origin,
            destination: s.destination,
            departureDate: s.departureDate,
          }));
          break;
      }

      if (searchParams.baggage) {
        requestBody.baggage = searchParams.baggage;
      }
      if (searchParams.seatPreferences) {
        requestBody.seatPreferences = searchParams.seatPreferences;
      }
      if (searchParams.holdOptions) {
        requestBody.holdOptions = searchParams.holdOptions;
      }
      if (searchParams.loyaltyPrefs) {
        requestBody.loyaltyPreferences = searchParams.loyaltyPrefs;
      }

      console.log('Duffel search request:', requestBody);

      const response = await apiFetch('/api/flights/search', {
        method: 'POST',
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();

      if (tripType === 'multi-city' && data.flightsBySegment) {
        return data.flightsBySegment.flat();
      }

      return data.flights || [];
    } catch (error) {
      console.error('Flight search error:', error);
      throw error;
    }
  }

  static async searchFlightSegment(segment: MultiCitySegment, passengers?: Passengers, cabinClass?: CabinClass): Promise<Flight[]> {
    return this.searchFlights({
      tripType: 'one-way',
      origin: segment.origin,
      destination: segment.destination,
      departureDate: segment.departureDate,
      passengers,
      cabinClass,
    });
  }
}
